import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchCards } from "../api";
import IMAGES from "../data/imageMap";
import { useSiteContent } from "../contexts/SiteContent";

const TABS = [
  { id: "life-sciences", label: "Life sciences and health" },
  { id: "earth", label: "Earth and sustainability" },
  { id: "energy-materials", label: "Energy and materials" },
  { id: "algorithmic", label: "Algorithmic foundations" },
];

export default function Resources() {
  const { t } = useSiteContent();
  const [cards, setCards] = useState([]);
  const [activeTab, setActiveTab] = useState("life-sciences");

  useEffect(() => {
    fetchCards("resources").then(setCards).catch(() => setCards([]));
  }, []);

  const visible = cards.filter((c) => c.resource_tab === activeTab);

  return (
    <section
      id="resources"
      data-testid="resources-section"
      className="bg-white py-16 md:py-24 lg:py-28"
    >
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="max-w-[820px]">
          <h2 className="text-[32px] md:text-[44px] font-semibold tracking-[-0.02em] leading-[1.1] text-neutral-900">
            {t("resources_intro.heading", "AI resources for the scientific community")}
          </h2>
          <p className="mt-4 text-[16px] md:text-[17px] text-neutral-600 leading-relaxed">
            {t(
              "resources_intro.subtitle",
              "Tools, datasets, and models we're sharing openly so researchers everywhere can build on this work."
            )}
          </p>
        </div>

        <div
          data-testid="resources-tabs"
          className="mt-10 flex gap-2 md:gap-3 overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0"
        >
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              data-testid={`resource-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`shrink-0 rounded-full h-10 px-5 text-[13px] font-medium transition-all ${
                activeTab === tab.id
                  ? "bg-neutral-900 text-white"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {visible.length === 0 ? (
            <div
              data-testid="resources-coming-soon"
              className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-12 text-center"
            >
              <p className="text-[18px] font-semibold text-neutral-900">Coming soon</p>
              <p className="mt-2 text-[14px] text-neutral-600 max-w-[420px] mx-auto">
                We&apos;re actively developing AI resources for this domain.
                Check back soon for tools, models, and datasets.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {visible.map((card) => {
                const detailTo = card.slug ? `/resources/${card.slug}` : null;
                return (
                  <div
                    key={card.id}
                    data-testid={`resource-card-${card.order}`}
                    className="group rounded-2xl bg-white border border-neutral-200 overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)]"
                  >
                    <div className="aspect-[4/3] w-full overflow-hidden bg-neutral-100">
                      <img
                        src={card.image_url || IMAGES.resourcesBySlug?.[card.slug] || IMAGES.resources[card.order]}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="text-[18px] font-semibold tracking-tight text-neutral-900 leading-snug">
                        {card.title}
                      </h3>
                      <p className="mt-2 text-[14px] text-neutral-600 leading-relaxed">
                        {card.blurb}
                      </p>
                      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                        {detailTo ? (
                          <Link
                            to={detailTo}
                            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-neutral-900"
                          >
                            Learn more
                            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                          </Link>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-neutral-900">
                            Learn more
                            <ArrowRight className="h-3.5 w-3.5" />
                          </span>
                        )}
                        {(card.links || []).map((l, i) => (
                          <a
                            key={i}
                            href={l.url || "#"}
                            target={l.url && /^https?:\/\//.test(l.url) ? "_blank" : undefined}
                            rel="noopener noreferrer"
                            data-testid={`resource-link-${card.order}-${i}`}
                            className="text-[13px] font-medium text-[#2d5fd4] hover:underline"
                          >
                            {l.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
